import { Box, Card, CardContent, CardHeader, Divider, FormControl, Link, styled, Typography, Stack, Select, MenuItem, InputLabel, TextField, Alert, Button, Chip, CircularProgress, CardActions } from '@mui/material';
import { orange } from '@mui/material/colors';
import axios from 'axios';
import React from 'react';
import OtpInput from 'react-otp-input';
import { toast, ToastContainer } from 'react-toastify';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';

const MFAContainer = styled(Stack)(({ theme }) => ({
    height: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: 'calc(50% - 20px)',
    maxWidth: '500px',
    margin: '0 auto',
    justifyContent: 'center',
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(4),
    },
    '&::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      zIndex: -1,
      inset: 0,
      backgroundImage: 'url(https://wordpress.idea4industry.com/wp-content/uploads/AdobeStock_443793149-speed-blue-1536x922.jpg)',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundColor:
        'radial-gradient(ellipse at 50% 50%, hsl(211, 89.70%, 7.60%), hsl(0, 11.10%, 10.60%))',
      ...theme.applyStyles('dark', {
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundImage: 'url(https://wordpress.idea4industry.com/wp-content/uploads/AdobeStock_443793149-speed-blue-1536x922.jpg)',
          backgroundColor:
              'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
      }),
    },
  }));

export default function MFA() {
    const [mfaMethod, setMfaMethod] = React.useState('');
    const [otp, setOtp] = React.useState('');
    const [time, setTime] = React.useState(600);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (time > 0) {
            let timer = setInterval(() => {
            setTime((time) => {
              if (time === 0) {
                clearInterval(timer);
                return 0;
              } else return time - 1;
            });
          }, 1000);
          return () => clearInterval(timer);
        }
    }, [time]);

    const handleSubmitClick = async () => {
        try{
            if (otp.length !== 6) {
                toast.error('Please enter a valid OTP code.', {position: 'top-center'});
                return;
            }

            setLoading(true);
            const csrfToken = await axios.get('/csrf/');
            await axios.post('/verify-otp/', {
                otp: otp,
                headers: {
                    'xsrfCookieName': 'csrftoken',
                    'X-CSRFToken': csrfToken.data.csrfToken
                },
            }).then(response => {
                toast.success(response.data.message, {position: 'top-center'});
                // Reset entity selection so post-login dashboard uses the default entity
                localStorage.removeItem('entity_id');
                localStorage.removeItem('entity_nm');
                localStorage.removeItem('user');
                setTimeout(() => {
                    window.location.href = '/';
                }, 100);
            }).catch(error => {
                setLoading(false);
                toast.error(error.response?.data?.message, {position: 'top-center'});
            });
        }catch(error){
            setLoading(false);
            toast.error('An error occurred while sending the OTP code. Please try again or contact with the administrator.', {position: 'top-center'});
        }
    }

    const handleMfaMethodChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        setMfaMethod(event.target.value as 'SMS' | 'Email');
        try{
            const csrfToken = await axios.get('/csrf/');
            await axios.post(`/send-otp-code/${event.target.value}/`, {
                headers: {
                    'xsrfCookieName': 'csrftoken',
                    'X-CSRFToken': csrfToken.data.csrfToken,
                    'Content-Type': 'application/json',
                },
            }).then(response => {
                toast.success(response.data.message, {position: 'top-center'});
            }).catch(error => {
                toast.error(error.response?.data?.message, {position: 'top-center'});
            });
        }catch(error){
            toast.error('An error occurred while sending the OTP code. Please try again or contact with the administrator.', {position: 'top-center'});
        }
    }

    const handleResendOtp = async () => {
        try{
            setOtp(''); // Clear the OTP input
            const csrfToken = await axios.get('/csrf/');
            await axios.post('/mfa/', {
                headers: {
                    'xsrfCookieName': 'csrftoken',
                    'X-CSRFToken': csrfToken.data.csrfToken,
                    'Content-Type': 'application/json',
                },
            }).then(response => {
                toast.success(response.data.message, {position: 'top-center'});
                setTime(600);
            }).catch(error => {
                toast.error(error.response?.data?.message, {position: 'top-center'});
            });
        }catch(error){
            toast.error('An error occurred while resending the OTP code. Please try again or contact with the administrator.', {position: 'top-center'});
        }
    }

    return (
        <MFAContainer direction="column" justifyContent="space-between">
            <ToastContainer />
            <Card variant="outlined" style={{ borderRadius: 5, width: '100%' }}>
                <CardHeader
                    title={
                        <Stack direction="column" alignItems="center" justifyContent="center" fontFamily='inherit' spacing={1}>
                            <img src="https://idea4industry.com/wp-content/uploads/cropped-IDEA-favicon-192x192.png" width="10%" height="10%" alt="IDEA Data Whispers" />
                            <Typography variant="h6" sx={{ fontWeight: 'bold', fontFamily: 'Trebuchet MS, sans-serif', maxWidth: '90%', textAlign: 'center', paddingTop: '5px', fontSize: 'clamp(1.1rem, 10vw, 1.2rem)' }}>
                            <Link href="/" style={{ textDecoration: 'none', color: 'inherit', fontFamily: 'Trebuchet MS, sans-serif' }}>IDEA Data Whispers</Link>
                            </Typography>
                        </Stack>
                    }
                    subheader={
                        <Typography variant="h5" color='text.secondary' sx={{ mt: 2, mb: -2, fontWeight: 'normal', textAlign: 'center', justifySelf: 'center', fontFamily: 'Trebuchet MS, sans-serif', justifyContent: 'center', fontSize: '0.9rem' }}>
                            Multi-Factor Authentication is required to access your account.<br/>
                            Please enter the OTP code sent to your phone number or e-mail account.
                        </Typography>
                    }
                />
                <Divider sx={{ width: '100%', margin: 0, padding: 1 }} />
                <CardContent sx={{ padding: 2, marginTop: 2, justifyContent: 'center'}}>
                    <Stack direction="column" alignItems="center" justifyContent="center" spacing={2} sx={{ mt: 0 }}>
                        {time !== 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', justifyContent: 'center', width: '100%', gap: 1 }}>
                                <Typography 
                                    variant="body1" 
                                    color='primary' 
                                    sx={{ 
                                        fontWeight: 'normal', 
                                        fontFamily: 'Trebuchet MS, sans-serif', 
                                        fontSize: 'clamp(1rem, 10vw, 1rem)' 
                                    }}
                                >
                                    <Chip 
                                        variant="outlined"
                                        color="primary"
                                        icon={<HourglassBottomIcon 
                                        sx={{ fontSize: 'clamp(1.3rem, 10vw, 1.3rem)', borderRadius: 2 }} />} 
                                        label={`${Math.floor(time / 60)}`.padStart(2, '0') + ":" + `${time % 60}`.padStart(2, '0') + " minutes remaining"}
                                    />
                                </Typography>
                        
                                <FormControl sx={{ margin: '0 auto' }}>
                                    <OtpInput
                                        value={otp}
                                        onChange={setOtp}
                                        numInputs={6}
                                        renderSeparator={<span>&nbsp;</span>}
                                        renderInput={(props) => <input type="text" {...props} />}
                                        shouldAutoFocus={true}
                                        inputStyle={{
                                            width: '35px',
                                            height: '35px',
                                            textAlign: 'center',
                                            fontSize: 'clamp(1rem, 10vw, 1rem)',
                                            fontWeight: 'bold',
                                            fontFamily: 'Trebuchet MS, sans-serif',
                                        }}
                                    />
                                </FormControl>
                            </Box>
                        )}
                        {time === 0 ? (
                            <Button variant="text" onClick={handleResendOtp} sx={{ textTransform: 'none' }}>Resend OTP</Button>
                        ) : (
                            <Button
                                onClick={handleSubmitClick}
                                variant="contained"
                                loading={loading}
                                loadingPosition="end"
                                loadingIndicator={<Typography sx={{ display: 'flex', textAlign: 'center', justifyContent: 'center', alignItems: 'center', gap: 1, opacity: 0.8 }}><CircularProgress variant="indeterminate" size={16} sx={{ color: 'white', margin: '0 auto' }} /></Typography>}
                                sx={{
                                    backgroundColor: orange[800],
                                    border: 0,
                                    boxShadow: 'none',
                                    textTransform: 'capitalize',
                                    fontSize: 'clamp(1.1rem, 10vw, 1rem)',
                                    margin: '4px auto'
                                }}
                            >
                                {!loading && 'Submit'}
                            </Button>
                        )}
                    </Stack>
                </CardContent>
                <CardActions sx={{ justifyContent: 'center' }}>
                    <Typography id="copyright-text" variant="h6" sx={{ fontFamily: 'inherit', textAlign: 'center', width: '100%', fontSize: '0.8rem', marginBottom: 2 }}>
                        {`© ${new Date().getFullYear()}, All rights reserved by IDEA`}
                    </Typography>
                </CardActions>
            </Card>
        </MFAContainer>
    );
}